// Özet stat kartları (M16 T14, kural 14): showChange=true göstergelerden orderIndex sırasıyla
// ilk 4; cari yıl değeri + önceki yıl + değişim rozeti (increaseIsGood yönüne göre renk).
// Yüzde göstergede "puan" farkı, diğerlerinde % değişim. Kırılım (spec §6.6): kart adayı
// "gösterge" tanımıyla (metrik ∨ kırılımlı toplam — grup/kategori değil; backend
// `countMetricIndicators` ile aynı) — türetilmiş toplam kart olabilir. `KpiCounterStrip`: DR5 sayaç
// şeridi adları (tek satır, kart değil), her sayaç kendi süzgecini açar.

import { changePercent, changeTone, type KpiResolvedGrid } from "@mahir/shared";
import { KPI_BREAKDOWN_COPY } from "../../lib/kpi-breakdown-copy";
import { isMetricIndicator } from "../../lib/kpi-grid-breakdown";
import { KPI_COPY, formatKpiCell } from "../../lib/kpi-ui";

export type KpiCounterFilter = "all" | "missing" | "approval" | "calcError";

/**
 * DR5 sayaç şeridi öğeleri ("6 gösterge · Veri bekleyen hücre: 2 · Onay bekleyen hücre: 1 ·
 * Hesaplanamayan toplam: 1"): sayılar çağırandan (grup satırı ve türetilmiş toplam "veri bekleyen"e
 * girmez); tıklama `onFilter`. Kırmızı yok — bekleyenler amber.
 */
export function KpiCounterStrip({
  counts,
  onFilter,
}: {
  counts: { indicators: number; missing: number; approval: number; calcError: number };
  onFilter: (f: KpiCounterFilter) => void;
}): JSX.Element {
  const C = KPI_BREAKDOWN_COPY.counters;
  const item = (f: KpiCounterFilter, text: string, warn: boolean): JSX.Element => (
    <button
      key={f}
      type="button"
      data-testid={`kpi-counter-${f}`}
      onClick={() => onFilter(f)}
      style={{ color: warn ? "var(--color-warning-text)" : "var(--color-text-2)" }}
    >
      {text}
    </button>
  );
  return (
    <>
      {[
        item("all", C.indicators(counts.indicators), false),
        item("missing", C.missingCells(counts.missing), false),
        item("approval", C.approvalCells(counts.approval), counts.approval > 0),
        item("calcError", C.calcErrorTotals(counts.calcError), counts.calcError > 0),
      ]}
    </>
  );
}

export function KpiStatCards({
  grid,
  year,
  prevYear,
}: {
  grid: KpiResolvedGrid;
  year: number;
  prevYear: number | null;
}): JSX.Element | null {
  const counted = (ind: KpiResolvedGrid["indicators"][number]): boolean =>
    isMetricIndicator(
      ind,
      grid.indicators.filter((c) => c.parentId === ind.id),
    );
  const picks = grid.indicators
    .filter((i) => i.showChange && counted(i))
    .sort((a, b) => a.orderIndex - b.orderIndex)
    .slice(0, 4);
  const hint = (
    <div
      className="text-xs mb-2"
      data-testid="kpi-stat-cards-hint"
      style={{ color: "var(--color-text-3)" }}
    >
      {picks.length === 0 ? KPI_COPY.ozetCards.none : KPI_COPY.ozetCards.hint}
    </div>
  );
  if (picks.length === 0) return <div className="mb-4">{hint}</div>;
  return (
    <div className="mb-4">
      {hint}
      <div
        className="grid gap-3"
        style={{ gridTemplateColumns: `repeat(${Math.min(4, picks.length)}, minmax(0, 1fr))` }}
      >
        {picks.map((ind) => {
          const cur = ind.consolidated?.[year];
          const prev = prevYear ? ind.consolidated?.[prevYear] : undefined;
          const isPct = ind.valueType === "percent";
          const d = formatKpiCell(cur, { percent: isPct });
          let changeText: string | null = null;
          let tone: "good" | "bad" | "neutral" = "neutral";
          if (
            cur?.status === "filled" &&
            prev?.status === "filled" &&
            typeof cur.value === "number" &&
            typeof prev.value === "number"
          ) {
            if (isPct) {
              const diff = cur.value - prev.value;
              changeText = `${diff >= 0 ? "▲" : "▼"} ${Math.abs(diff).toLocaleString("tr-TR", { maximumFractionDigits: 1 })} puan`;
              tone = changeTone(diff, ind.increaseIsGood);
            } else {
              const pct = changePercent(cur.value, prev.value);
              if (pct !== null) {
                changeText = `${pct >= 0 ? "▲" : "▼"} %${Math.abs(pct).toLocaleString("tr-TR", { maximumFractionDigits: 1 })}`;
                tone = changeTone(pct, ind.increaseIsGood);
              }
            }
          }
          return (
            <div
              key={ind.id}
              className="rounded-xl px-4 py-3"
              style={{
                background: "var(--color-surface)",
                border: "1px solid var(--color-border)",
              }}
            >
              <div
                style={{
                  fontSize: "0.68rem",
                  textTransform: "uppercase",
                  letterSpacing: "0.07em",
                  fontWeight: 700,
                  color: "var(--color-text-2)",
                }}
                title={ind.label}
              >
                {ind.label}
              </div>
              <div
                style={{
                  // P3-14: "HESAPLANAMADI" gibi durum metinleri 1.6rem'de kırpılıyordu → küçük rozet
                  fontSize: d.tone === "value" ? "1.6rem" : "0.8rem",
                  fontWeight: 700,
                  color: d.tone === "value" ? "var(--color-primary)" : "var(--color-text-3)",
                  lineHeight: d.tone === "value" ? 1.2 : 1.4,
                  marginTop: 6,
                  minHeight: "1.9rem",
                  display: "flex",
                  alignItems: "center",
                  flexWrap: "wrap",
                  overflowWrap: "anywhere",
                  letterSpacing: d.tone === "value" ? undefined : "0.04em",
                }}
                title={d.tone === "value" ? undefined : d.text}
              >
                {d.text}
                {d.tone === "value" && !isPct && ind.unit ? (
                  <small
                    style={{
                      fontSize: "0.8rem",
                      color: "var(--color-text-2)",
                      fontWeight: 600,
                      marginLeft: 4,
                    }}
                  >
                    {ind.unit}
                  </small>
                ) : null}
              </div>
              <div className="text-xs mt-1">
                {changeText ? (
                  <span
                    style={{
                      color:
                        tone === "good"
                          ? "var(--color-success)"
                          : tone === "bad"
                            ? "var(--color-error)"
                            : "var(--color-text-2)",
                      fontWeight: 600,
                    }}
                  >
                    {changeText}
                  </span>
                ) : (
                  <span style={{ color: "var(--color-text-3)" }}>—</span>
                )}
                {prevYear ? (
                  <span style={{ color: "var(--color-text-3)" }}>
                    {" "}
                    · {prevYear}: {formatKpiCell(prev, { percent: isPct }).text}
                  </span>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
